import { createContext, useContext } from "react";
import type {
  ApiClient,
  ChatConversation,
  Entity,
  Me,
  Meeting,
  Organization,
  OrganizationProfileEdit,
  PersonCard,
  Recording,
  ReviewCounts,
  SetupState,
  SourceMaterial,
  UserSettings,
  WorldViewActivity,
  WorldViewIndex,
} from "./api";

export interface AppContextValue {
  api: ApiClient;
  /** Who is signed in and which organization they work in, loaded once at start-up. */
  me: Me;
  /**
   * ARCH-8 — developer mode, as it stands right now.
   *
   * Held here rather than read from `me.settings` because it is toggled in Settings →
   * Advanced and read on the recording detail, and a copy loaded once at start-up would
   * mean turning it on did nothing until a reload.
   */
  settings: UserSettings | null;
  updateSettings: (patch: Partial<UserSettings>) => Promise<void>;
  /**
   * UI-18 — the organization as it stands right now, including the general information
   * about it that every prompt in the pipeline is given.
   *
   * Held here for the same reason `settings` is, and it is the same bug avoided: Account
   * settings is where the name and description are edited, `me` is loaded once at
   * start-up, and a page reading the start-up copy would show the old name the moment
   * somebody navigated away and came back.
   */
  organization: Organization | null;
  updateOrganizationProfile: (profile: OrganizationProfileEdit) => Promise<void>;
  /**
   * UI-13 — what still stops a recording running end to end. Held here because the
   * Setup page and the rail's warning dot read the same answer, and a dot that
   * disagrees with the page is worse than no dot.
   */
  setup: SetupState | null;
  refreshSetup: () => void;
  /**
   * INS-4 and AUTO-1 — how much is waiting on the owner, for the rail's Review-queue badge
   * and the queue navigator's "By type" counts. Held here so the badge, the panel and the
   * page read one answer, and refreshed when a card is decided so all three keep up without
   * a reload.
   */
  reviewCounts: ReviewCounts | null;
  refreshReview: () => void;
  /**
   * SEC-1 — end this browser's session (UI-13's Account section).
   *
   * Threaded through here rather than done in the settings page, because the Supabase
   * client lives at the root of the app: a second one made in a page would sign out of a
   * session this one does not own, and the first would carry on as though nothing had
   * happened. Absent under a test session, which has its own way out.
   */
  signOut?: () => void;
  /** What the capture pages' navigator shows under "Recently added". */
  recentSources: SourceMaterial[];
  /** Called by whatever just added a source, so the panel does not lag behind it. */
  refreshRecentSources: () => void;
  /**
   * UI-4's list, polled while anything on it is still moving. Held here rather than in
   * the page because the navigator panel shows the same rows with the same status
   * dots, and two copies would disagree with each other a couple of seconds at a time.
   */
  recordings: Recording[];
  recordingsLoading: boolean;
  recordingsProblem: string | null;
  refreshRecordings: () => void;
  /**
   * The recordings navigator's second level — each recording's meetings, keyed by
   * recording id, so it can nest them underneath it and the search bar can match a
   * meeting's name as well as a recording's.
   */
  meetingsByRecording: Record<string, Meeting[]>;
  refreshRecordingMeetings: () => void;
  /**
   * UI-6 — the organization's entities, held once so the manager and the navigator panel
   * (per-type counts, "in this view") read one set. Refreshed after every add, edit or
   * remove rather than polled: an entity changes only when somebody here changes it.
   */
  entities: Entity[];
  entitiesLoading: boolean;
  entitiesProblem: string | null;
  refreshEntities: () => void;
  /**
   * UI-8 — the organization's people, held once so the gallery and the navigator panel read
   * one set. Separate from `entities` rather than filtered out of it because a card carries
   * counts the entity list does not have: the recordings somebody was heard in, and the
   * approved insights they appear in.
   */
  people: PersonCard[];
  peopleLoading: boolean;
  peopleProblem: string | null;
  refreshPeople: () => void;
  /**
   * UI-10 — the world view's subject index, held once so the index page and the navigator
   * panel ("By subject type", "Pages", the contradiction count) read one answer. Refreshed
   * when an approval starts a synthesis pass, because that is the only thing this browser
   * does that can change it.
   */
  worldView: WorldViewIndex | null;
  worldViewLoading: boolean;
  worldViewProblem: string | null;
  refreshWorldView: () => void;
  /**
   * UI-23 — whether a pass is running, the backlog waiting for the next one (WORLD-11),
   * and the last one that finished. Polled only while something is running; an approve
   * answer carrying `synthesizing: true` calls `refreshWorldViewActivity` to start that
   * polling immediately rather than waiting for its next natural tick.
   */
  worldViewActivity: WorldViewActivity | null;
  worldViewActivityProblem: string | null;
  refreshWorldViewActivity: () => void;
  /**
   * CHAT-2 — the member's conversations, held once so the navigator panel lists the same
   * threads the page opens. `chatAvailable` is false on a deployment with no MCP server,
   * where the Server maps no chat routes at all.
   */
  conversations: ChatConversation[];
  conversationsLoading: boolean;
  conversationsProblem: string | null;
  chatAvailable: boolean;
  refreshConversations: () => void;
}

const AppContext = createContext<AppContextValue | null>(null);

/**
 * The Server client and the signed-in identity, handed to whichever pages need them.
 *
 * Threaded through context rather than down props because the shell renders its pages
 * through a router outlet — there is no prop path from the app root to a capture page
 * — and because a page that needs the client should say so by asking for it, not by
 * having it passed through three components that do not care.
 */
export function AppProvider({ value, children }: { value: AppContextValue; children: React.ReactNode }) {
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

/**
 * Throws outside a provider rather than returning null. A page that reaches the Server
 * cannot do anything sensible without a client, and a missing provider is a wiring
 * mistake that should fail loudly in development rather than render an empty screen.
 */
export function useApp(): AppContextValue {
  const value = useContext(AppContext);
  if (!value) throw new Error("useApp was called outside an AppProvider.");

  return value;
}
